// Statusendring med e-posten som hører til, vist før den sendes.
//
// Kundens ønske 11.09.2026: e-postene må ofte tilpasses den enkelte ordren, og
// i dag er valget alt-eller-ingenting — enten går malen ut ordrett, eller så
// går den ikke ut. Her får admin se nøyaktig hva kunden ville fått, rette det,
// og sende det sammen med statusendringen.
//
// Koblingen status → mal kommer fra automasjonsreglene, ikke fra en egen liste
// her, så dialogen og automatikken aldri kan være uenige om hvilken e-post som
// hører til hvilken status.
//
// Én dialog for begge ordresidene. To kopier av denne logikken ville garantert
// glidd fra hverandre.
import { useCallback, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import {
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  ListSubheader,
  MenuItem,
  Select,
} from "@mui/material";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CodeIcon from "@mui/icons-material/Code";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";
import HtmlEmailEditor from "components/HtmlEmailEditor";
import SoftInput from "components/SoftInput";
import { notifyCenter } from "utils/toast";
import { statusLabel } from "utils/statusLabels";
import VendorRequestCompose from "components/VendorRequestPanel/Compose";
import {
  fetchStatusList,
  changeOrderStatus,
  previewStatusEmail,
  changeStatusWithEmail,
  changeStatusWithoutEmail,
} from "api/apiService";

// Soft UI tvinger display:grid på MuiInputBase-root, som krymper klikkflaten.
// Temaet setter også width: max-content !important på selve feltet, så uten
// flex/width her er bare teksten «Velg status …» klikkbar, og et klikk ellers
// i feltet gjør ingenting.
const SELECT_SX = {
  height: 42,
  "&.MuiInputBase-root": {
    display: "flex !important",
    placeItems: "initial !important",
    padding: "0 !important",
  },
  "& .MuiSelect-select": {
    display: "flex !important",
    alignItems: "center !important",
    minHeight: "42px !important",
    boxSizing: "border-box",
    padding: "0 32px 0 14px !important",
    flex: 1,
    width: "100% !important",
  },
};

// «Venter på tilbud» sender forespørselen til produsentene. Statusen settes av
// selve utsendingen, så dialogen viser utsendingsskjemaet i stedet for en mal.
const REQUEST_STATUS = "awaiting-quote";

function StatusChangeDialog({ open, orderId, currentStatus, initialStatus, onClose, onChanged }) {
  const [statuses, setStatuses] = useState([]);
  const [nextStatus, setNextStatus] = useState("");
  const [busy, setBusy] = useState(false);

  const [checking, setChecking] = useState(false);
  const [preview, setPreview] = useState(null); // null = ikke sjekket ennå
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [showSource, setShowSource] = useState(false);

  useEffect(() => {
    if (!open || statuses.length) return;
    fetchStatusList()
      .then((res) => {
        if (Array.isArray(res?.status_list)) setStatuses(res.status_list);
      })
      .catch(() => notifyCenter("error", "Kunne ikke hente statuslisten."));
  }, [open, statuses.length]);

  // Nullstill når dialogen lukkes, ellers viser neste åpning forrige ordres
  // e-post i et halvsekund.
  useEffect(() => {
    if (open) return;
    setNextStatus("");
    setPreview(null);
    setSubject("");
    setMessage("");
    setShowSource(false);
  }, [open]);

  // Siste status admin valgte. Et tregt svar for en status man allerede har
  // valgt bort, skal ikke legge seg over e-posten til den nye — da ble feil
  // e-post sendt sammen med riktig status.
  const latestPick = useRef("");

  const pickStatus = useCallback(
    async (value) => {
      latestPick.current = value;
      setNextStatus(value);
      setPreview(null);
      if (!value || value === REQUEST_STATUS) {
        setChecking(false);
        return;
      }

      setChecking(true);
      try {
        const res = await previewStatusEmail(orderId, value);
        if (latestPick.current !== value) return;
        setPreview(res || { ok: false });
        if (res?.has_email) {
          setSubject(res.subject || "");
          setMessage(res.message || "");
        }
      } catch (e) {
        if (latestPick.current === value) setPreview({ ok: false });
      } finally {
        if (latestPick.current === value) setChecking(false);
      }
    },
    [orderId]
  );

  // Åpnet med en status ferdig valgt — f.eks. «Klar til betaling» rett etter at
  // admin har valgt en fastpris. Da vises e-posten med én gang.
  useEffect(() => {
    if (open && initialStatus && !nextStatus) pickStatus(initialStatus);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialStatus]);

  // Etiketten fra statuslisten (den admin kan ha endret), ellers standarden.
  const labelOf = (value) => statuses.find((s) => s.value === value)?.label || statusLabel(value);

  const isRequest = nextStatus === REQUEST_STATUS;
  const recipients = (preview?.recipients || []).filter((r) => r.email);
  const missing = (preview?.recipients || []).filter((r) => !r.email);
  const hasEmail = !!preview?.has_email;

  const finish = (label) => {
    notifyCenter("success", label);
    onChanged?.();
    onClose?.();
  };

  const sendAndChange = async () => {
    if (!recipients.length) return;
    setBusy(true);
    try {
      const res = await changeStatusWithEmail(orderId, nextStatus, {
        subject,
        message,
        emails: recipients.map((r) => r.email),
        emailType: preview?.email_type,
      });
      if (res?.ok) {
        const failed = res.failed_recipients || [];
        if (failed.length) {
          // Minst én fikk den, så statusen er endret — men admin må vite hvem
          // som ikke fikk den.
          notifyCenter(
            "warning",
            `E-posten gikk ikke til ${failed.map((f) => f.email).join(", ")}. Status er endret.`
          );
        }
        finish(`E-post sendt til ${(res.recipients || []).length} mottaker(e), og status er endret.`);
      } else if (res?.email_sent) {
        // E-posten er ute selv om statusen ikke ble endret. Å si «feilet» ville
        // fått admin til å prøve igjen og sende den samme e-posten to ganger.
        notifyCenter("warning", res.message || "E-posten ble sendt, men statusen ble ikke endret.");
        onChanged?.();
      } else {
        notifyCenter("error", res?.message || "Kunne ikke sende e-posten. Statusen er uendret.");
      }
    } catch (e) {
      notifyCenter("error", "Kunne ikke sende e-posten. Statusen er uendret.");
    } finally {
      setBusy(false);
    }
  };

  // `withoutEmail`: «Endre status uten e-post» på en status som har e-post. Da
  // skal kun statusen endres — en vanlig statusendring kjører e-postregelen,
  // og e-posten gikk ut via cron selv om admin hadde valgt den bort.
  const changeOnly = async ({ withoutEmail = false } = {}) => {
    setBusy(true);
    try {
      const res = withoutEmail
        ? await changeStatusWithoutEmail(orderId, nextStatus)
        : await changeOrderStatus(orderId, nextStatus);
      if (res?.ok === true) finish(withoutEmail ? "Status oppdatert. Ingen e-post sendt." : "Status oppdatert.");
      else notifyCenter("error", res?.message || "Statusendring feilet.");
    } catch (e) {
      notifyCenter("error", "Statusendring feilet.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => !busy && onClose?.()} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontSize: 18, fontWeight: 700 }}>Endre status</DialogTitle>
      <DialogContent>
        {/* Nåværende status sto bare inne i nedtrekket, merket «(nåværende)».
            Admin skal se hva ordren går fra uten å åpne listen. */}
        {currentStatus && (
          <SoftBox display="flex" alignItems="center" flexWrap="wrap" gap={1} mb={2}>
            <SoftTypography variant="caption" fontWeight="bold">
              Nåværende status:
            </SoftTypography>
            <Chip size="small" label={labelOf(currentStatus)} />
            {nextStatus && (
              <>
                <SoftTypography variant="caption" color="text" aria-hidden="true">
                  →
                </SoftTypography>
                <Chip size="small" color="info" label={labelOf(nextStatus)} />
              </>
            )}
          </SoftBox>
        )}
        <SoftTypography variant="caption" fontWeight="bold" display="block" mb={0.5}>
          Ny status
        </SoftTypography>
        <FormControl fullWidth>
          <Select
            value={nextStatus}
            onChange={(e) => pickStatus(e.target.value)}
            displayEmpty
            sx={SELECT_SX}
          >
            <MenuItem value="">Velg status …</MenuItem>
            {/* Statusrekken fra Utvikling | BMAx s. 8 først, i den rekkefølgen.
                De eldre WooCommerce-statusene står under for ordre som
                fortsatt har en av dem. MUI Select tar ikke fragmenter, derfor
                en flat liste. */}
            {[
              ...statuses.filter((s) => !s.legacy),
              ...(statuses.some((s) => s.legacy) ? [{ header: true }] : []),
              ...statuses.filter((s) => s.legacy),
            ].map((s) =>
              s.header ? (
                <ListSubheader key="legacy-header" sx={{ fontSize: 12, lineHeight: "32px" }}>
                  Eldre statuser
                </ListSubheader>
              ) : (
                <MenuItem key={s.value} value={s.value} disabled={s.value === currentStatus}>
                  {s.label}
                  {s.value === currentStatus ? " (nåværende)" : ""}
                </MenuItem>
              )
            )}
          </Select>
        </FormControl>

        {checking && (
          <SoftBox display="flex" alignItems="center" gap={1} mt={2}>
            <CircularProgress size={16} />
            <SoftTypography variant="caption" color="text">
              Ser etter e-post for denne statusen …
            </SoftTypography>
          </SoftBox>
        )}

        {/* Utvikling | BMAx s. 5 pkt. 3: «Alternativt kan vi endre status til
            'Venter på tilbud', slik at den tilknyttede e-postmalen åpnes
            automatisk. Der kan vi velge hvilke produsenter forespørselen skal
            sendes til, redigere e-posten ved behov og deretter trykke Send.» */}
        {isRequest && (
          <SoftBox mt={2}>
            <Divider />
            <VendorRequestCompose
              orderId={orderId}
              withEmailEditor
              onCancel={onClose}
              onSent={() => {
                onChanged?.();
                onClose?.();
              }}
            />
          </SoftBox>
        )}

        {/* Ingen mal er det normale: de fleste statuser sender ingenting. Da
            skal dialogen si det og ikke spørre om noe. */}
        {!checking && preview && !hasEmail && (
          <SoftBox
            mt={2}
            p={2}
            sx={{ backgroundColor: "#f4f6f9", borderRadius: 2, border: "1px solid #e6ecf3" }}
          >
            <SoftTypography variant="caption" color="text">
              {preview.problem ? (
                <>
                  <WarningAmberIcon sx={{ fontSize: 15, mr: 0.5, color: "#c2410c", verticalAlign: "text-bottom" }} />
                  {preview.problem}
                </>
              ) : (
                "Ingen e-post er knyttet til denne statusen. Ordren flyttes uten at noen varsles."
              )}
            </SoftTypography>
          </SoftBox>
        )}

        {!checking && hasEmail && (
          <SoftBox mt={2}>
            <Divider />
            <SoftBox display="flex" alignItems="center" gap={1} mt={2} mb={1} flexWrap="wrap">
              <MailOutlineIcon sx={{ fontSize: 18, color: "#1d4ed8" }} />
              <SoftTypography variant="button" fontWeight="bold">
                {preview.template_name || "E-post"}
              </SoftTypography>
              {recipients.map((r) => (
                <Chip key={r.role} size="small" variant="outlined" label={`${r.role}: ${r.email}`} />
              ))}
              {missing.map((r) => (
                <Chip
                  key={r.role}
                  size="small"
                  color="warning"
                  variant="outlined"
                  label={`${r.role}: ingen adresse`}
                />
              ))}
            </SoftBox>

            {!!preview.other_rules?.length && (
              <SoftTypography variant="caption" sx={{ color: "#c2410c", display: "block", mb: 1 }}>
                Merk: {preview.other_rules.length} annen regel er også koblet til denne statusen
                ({preview.other_rules.join(", ")}). Den e-posten sendes ikke når du endrer status her.
              </SoftTypography>
            )}

            {!!preview.later_rules?.length && (
              <SoftTypography variant="caption" color="text" display="block" mb={1}>
                Sendes automatisk senere hvis ordren fortsatt står i statusen: {preview.later_rules.join(", ")}.
              </SoftTypography>
            )}

            <SoftTypography variant="caption" fontWeight="bold" display="block" mb={0.5}>
              Emne
            </SoftTypography>
            <SoftInput value={subject} onChange={(e) => setSubject(e.target.value)} />

            <SoftBox display="flex" alignItems="center" justifyContent="space-between" mt={1.5} mb={0.5}>
              <SoftTypography variant="caption" fontWeight="bold">
                Innhold
              </SoftTypography>
              <SoftButton
                size="small"
                variant="text"
                color="secondary"
                onClick={() => setShowSource((v) => !v)}
              >
                {showSource ? (
                  <>
                    <VisibilityIcon sx={{ fontSize: 15, mr: 0.5 }} /> Vis e-posten
                  </>
                ) : (
                  <>
                    <CodeIcon sx={{ fontSize: 15, mr: 0.5 }} /> Vis HTML
                  </>
                )}
              </SoftButton>
            </SoftBox>

            {showSource ? (
              <SoftInput
                multiline
                rows={12}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
              />
            ) : (
              // Samme HTML som sendes, uten e-postrammen — redigerbar på stedet,
              // så admin slipper å lete i HTML-en for å rette en setning.
              <HtmlEmailEditor value={message} onChange={setMessage} />
            )}
            <SoftTypography variant="caption" color="text" display="block" mt={1}>
              Klikk i teksten for å rette den. E-posten sendes som den står her. Plassholderne er
              allerede fylt inn for denne ordren.
            </SoftTypography>
          </SoftBox>
        )}

        {/* Tre valg når statusen har en e-post: avbryt alt, endre bare statusen,
            eller send e-posten og endre statusen. Utvikling | BMAx, Prosess 1
            pkt. 2 sier «Hvis man klikker Avbryt, skal kun status endres og
            e-post ikke sendes» — men da fantes det ingen synlig vei ut uten å
            endre statusen, og Avbryt gjorde noe annet enn det heter. Valget
            «uten e-post» har derfor fått sin egen knapp. */}
        {!isRequest && (
        <SoftBox mt={3} display="flex" gap={1} justifyContent="flex-end" flexWrap="wrap" alignItems="center">
          <SoftButton size="small" variant="text" color="secondary" disabled={busy} onClick={onClose} sx={{ mr: "auto" }}>
            Avbryt
          </SoftButton>
          {hasEmail && (
            <SoftButton
              size="small"
              variant="outlined"
              color="info"
              disabled={!nextStatus || busy}
              onClick={() => changeOnly({ withoutEmail: true })}
            >
              Endre status uten e-post
            </SoftButton>
          )}
          <SoftButton
            size="small"
            variant="gradient"
            color={hasEmail ? "info" : "success"}
            disabled={!nextStatus || busy || (hasEmail && !recipients.length)}
            onClick={hasEmail ? sendAndChange : () => changeOnly()}
          >
            {busy
              ? "Arbeider …"
              : hasEmail
              ? "Send e-post og endre status"
              : "Endre status"}
          </SoftButton>
        </SoftBox>
        )}
      </DialogContent>
    </Dialog>
  );
}

StatusChangeDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  orderId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  currentStatus: PropTypes.string,
  initialStatus: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onChanged: PropTypes.func,
};

StatusChangeDialog.defaultProps = { currentStatus: "", initialStatus: "", onChanged: null };

export default StatusChangeDialog;
